// What the workspace has installed (02-DESIGN/14-solutions/03-the-console.md
// § the declaration): read on the installed list's verb when the console
// connects and on every visit to Solutions, and held once for the rail and
// the pages, so that a solution's panels are nested under Solutions as soon
// as it is installed. A read that fails leaves the rail with nothing nested;
// the Solutions page shows the refusal.

import { useEffect, useSyncExternalStore } from "react";

import type { Bus } from "../../platform/bus.ts";
import {
  RecordState,
  SERVICE,
  Subjects,
  type InstalledListReply,
  type Record_,
} from "../../wire/solutions.ts";

/**
 * OBSERVABILITY is the platform's observability solution, by name. Its panels
 * are the *Audit & usage* entry under Administration and are not nested under
 * Solutions (hq decision 0048).
 */
export const OBSERVABILITY = "observability";

interface Held {
  bus: Bus | undefined;
  records: readonly Record_[];
}

let held: Held = { bus: undefined, records: [] };
const listeners = new Set<() => void>();

function publish(next: Held) {
  held = next;
  for (const l of listeners) {
    l();
  }
}

/** readInstalled reads the install records, removed ones left out, and keeps them for the rail. */
export async function readInstalled(bus: Bus): Promise<Record_[]> {
  const reply = await bus.call<InstalledListReply>(SERVICE, Subjects.installedList(), undefined);
  const records = reply.solutions.filter((r) => r.state !== RecordState.removed);
  publish({ bus, records });
  return records;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/**
 * useInstalled is what the workspace has installed as last read, read once
 * for a connection the first time a component asks.
 */
export function useInstalled(bus: Bus): readonly Record_[] {
  const now = useSyncExternalStore(subscribe, () => held);
  useEffect(() => {
    if (held.bus !== bus) {
      readInstalled(bus).catch(() => {
        publish({ bus, records: [] });
      });
    }
  }, [bus]);
  return now.bus === bus ? now.records : [];
}
